import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CircleStop,
  History,
  MessageSquarePlus,
  Mic,
  MicOff,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import { ErpOffcanvas, ErpButton, ErpAlert, ErpSpinner, ErpDiv, ErpSpan, ErpButtonRaw, ErpFormRaw, ErpTextareaRaw } from '@/components/erp'
import { EmptyState as ErpEmptyState } from '@/components/erp/ErpEmptyState'
import AiMessageBubble from './AiMessageBubble'
import AiActionApprovalModal from './AiActionApprovalModal'
import { useAiApprovals } from '@/hooks/useAiApprovals'
import { useAiVoice } from '@/hooks/useAiVoice'
import { streamAiChat } from '@/services/aiChatClient'
import {
  useGetAiConversationsQuery,
  useDeleteAiConversationMutation,
  useGetAiConversationQuery,
  useDispatchAiAnalysisMutation,
} from '@/redux/api/aiApi'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  appendDelta,
  appendUserMessage,
  clearMessages,
  closeDrawer,
  finishAssistantMessage,
  hydrateMessages,
  setActiveConversation,
  setApprovalPrompt,
  setHistoryPanelOpen,
  setStreamError,
  startAssistantMessage,
} from '@/redux/slices/aiSlice'
import { useSwal } from '@/hooks/useSwal'

let messageSeq = 0
const nextMessageId = () => `ai-msg-${Date.now()}-${messageSeq++}`

/**
 * Global AI Copilot drawer: streaming chat with the POS copilot agent,
 * conversation history, voice input, suggestions, and the human approval
 * modal for mutating actions. Built exclusively from erp wrappers +
 * Tailwind utilities, RTL-aware, i18n en/ar.
 */
export const AiCopilotDrawer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const swal = useSwal()
  const dispatch = useAppDispatch()
  const ai = useAppSelector((state) => state.ai)

  const [input, setInput] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const { data: conversationsData } = useGetAiConversationsQuery(undefined, {
    skip: !ai.drawerOpen,
  })
  const { data: activeConversation } = useGetAiConversationQuery(ai.activeConversationId ?? '', {
    skip: !ai.activeConversationId,
  })
  const [deleteAiConversation] = useDeleteAiConversationMutation()
  const [dispatchAiAnalysis] = useDispatchAiAnalysisMutation()

  useAiApprovals(ai.drawerOpen)

  const voice = useAiVoice({
    lang: i18n.language,
    onTranscript: (text) => setInput((prev) => (prev ? `${prev} ${text}` : text)),
  })

  // Hydrate messages when an older conversation is opened.
  useEffect(() => {
    const data = activeConversation?.data
    if (!data?.id || !Array.isArray(data.messages)) return
    dispatch(
      hydrateMessages({
        conversationId: data.id,
        messages: data.messages.map((message: any) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          createdAt: message.created_at,
        })),
      }),
    )
  }, [activeConversation, dispatch])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [ai.messages])

  // Closing the drawer mid-stream must cancel the in-flight fetch, otherwise
  // it keeps streaming and later dispatches appendDelta/finishAssistantMessage
  // into a closed drawer. streamAiChat maps an AbortError to onDone(), which
  // finalizes the assistant message and resets isStreaming.
  useEffect(() => {
    if (!ai.drawerOpen) {
      abortRef.current?.abort()
    }
  }, [ai.drawerOpen])

  // Same guarantee on unmount: never leave a live stream behind.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const sendMessage = async (text?: string) => {
    const message = (text ?? input).trim()
    if (!message || ai.isStreaming) return

    setInput('')

    dispatch(appendUserMessage({ id: nextMessageId(), role: 'user', content: message, createdAt: new Date().toISOString() }))
    dispatch(startAssistantMessage(nextMessageId()))

    const controller = new AbortController()
    abortRef.current = controller

    await streamAiChat(message, ai.activeConversationId, {
      signal: controller.signal,
      lang: i18n.language?.startsWith('ar') ? 'ar' : 'en',
      onTextDelta: (delta) => dispatch(appendDelta(delta)),
      onApprovalRequest: () => {
        /* arrives via the Echo channel / pending-approvals fetch */
      },
      onDone: () => dispatch(finishAssistantMessage()),
      onError: (errorMessage) => dispatch(setStreamError(errorMessage)),
    })

    abortRef.current = null
  }

  const stopStreaming = () => {
    abortRef.current?.abort()
    dispatch(finishAssistantMessage())
  }

  const startNewConversation = () => {
    if (ai.isStreaming) stopStreaming()
    dispatch(clearMessages())
    dispatch(setHistoryPanelOpen(false))
  }

  const openConversation = (id: string) => {
    if (ai.isStreaming) stopStreaming()
    dispatch(setActiveConversation(id))
  }

  const removeConversation = async (id: string) => {
    const result = await swal.deleteConfirm()
    if (!(result as any)?.isConfirmed) return
    try {
      await deleteAiConversation(id).unwrap()
      if (ai.activeConversationId === id) dispatch(clearMessages())
    } catch (error) {
      swal.toastError((error as any)?.data?.message ?? t('AI_StreamError'))
    }
  }

  const runDeepAnalysis = async () => {
    const topic = ai.messages.filter((m) => m.role === 'user').at(-1)?.content
    if (!topic) return
    try {
      await dispatchAiAnalysis(topic).unwrap()
      swal.toastSuccess(t('AI_BackgroundStarted'))
    } catch {
      swal.toastError(t('AI_StreamError'))
    }
  }

  const suggestions = [
    t('AI_Suggestion_Sales'),
    t('AI_Suggestion_LowStock'),
    t('AI_Suggestion_TopCustomers'),
    t('AI_Suggestion_Payables'),
  ]

  const conversations: Array<{ id: string; title: string; updated_at: string }> =
    conversationsData?.data ?? []

  return (
    <ErpOffcanvas
      modelValue={ai.drawerOpen}
      onUpdateModelValue={(open) => { if (!open) dispatch(closeDrawer()) }}
      onHide={() => dispatch(closeDrawer())}
      title={t('AI_Assistant')}
      placement="end"
      width={440}
      noHeader={false}
    >
      <ErpDiv className="flex h-full flex-col" dir={i18n.dir()}>
        {ai.streamError && (
          <ErpDiv className="px-3 pt-2">
            <ErpAlert variant="danger" dismissible onDismissed={() => dispatch(setStreamError(null))}>
              {ai.streamError}
            </ErpAlert>
          </ErpDiv>
        )}

        {ai.historyPanelOpen ? (
          <ErpDiv className="flex-1 overflow-auto p-3">
            <ErpDiv className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('AI_History')}</h3>
              <ErpDiv className="flex gap-1">
                <ErpButton size="sm" variant="outline" onClick={startNewConversation}>
                  <MessageSquarePlus className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
                  {t('AI_NewConversation')}
                </ErpButton>
                <ErpButton size="sm" variant="ghost" onClick={() => dispatch(setHistoryPanelOpen(false))}>
                  <X className="h-4 w-4" />
                </ErpButton>
              </ErpDiv>
            </ErpDiv>

            {conversations.length === 0 ? (
              <ErpEmptyState title={t('AI_EmptyState')} />
            ) : (
              <ul className="space-y-1">
                {conversations.map((conversation) => (
                  <li
                    key={conversation.id}
                    className={`group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-muted ${
                      ai.activeConversationId === conversation.id ? 'bg-muted' : ''
                    }`}
                    onClick={() => openConversation(conversation.id)}
                  >
                    <ErpSpan className="truncate">{conversation.title || t('AI_NewConversation')}</ErpSpan>
                    <ErpButtonRaw
                      type="button"
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={(event) => {
                        event.stopPropagation()
                        removeConversation(conversation.id)
                      }}
                      aria-label={t('AI_DeleteConversation')}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </ErpButtonRaw>
                  </li>
                ))}
              </ul>
            )}
          </ErpDiv>
        ) : (
          <ErpDiv ref={scrollRef} className="flex-1 overflow-auto p-3">
            {ai.messages.length === 0 ? (
              <ErpDiv className="flex h-full flex-col items-center justify-center gap-4">
                <ErpEmptyState
                  title={t('AI_EmptyState')}
                  icon="inbox"
                />
                <ErpDiv className="flex flex-wrap justify-center gap-2">
                  {suggestions.map((suggestion) => (
                    <ErpButtonRaw
                      key={suggestion}
                      type="button"
                      className="rounded-full border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                      onClick={() => sendMessage(suggestion)}
                    >
                      {suggestion}
                    </ErpButtonRaw>
                  ))}
                </ErpDiv>
              </ErpDiv>
            ) : (
              <>
                {ai.messages.map((message) => (
                  <AiMessageBubble key={message.id} message={message} />
                ))}
                {ai.isStreaming && (
                  <ErpDiv className="flex items-center gap-2 px-2 text-xs text-muted-foreground">
                    <ErpSpinner size="small" />
                    {t('AI_Thinking')}
                  </ErpDiv>
                )}
              </>
            )}
          </ErpDiv>
        )}

        <ErpDiv className="border-t p-3">
          <ErpFormRaw
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              sendMessage()
            }}
          >
            <ErpButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => dispatch(setHistoryPanelOpen(!ai.historyPanelOpen))}
              aria-label={t('AI_History')}
            >
              <History className="h-4 w-4" />
            </ErpButton>

            {voice.supported && (
              <ErpButton
                type="button"
                variant={voice.isListening ? 'danger' : 'ghost'}
                size="sm"
                onClick={() => (voice.isListening ? voice.stop() : voice.start())}
                aria-label={voice.isListening ? t('AI_VoiceStop') : t('AI_VoiceStart')}
              >
                {voice.isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </ErpButton>
            )}

            <ErpTextareaRaw
              className="h-10 max-h-32 flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
              placeholder={t('AI_AskAnything')}
              value={input}
              rows={1}
              dir="auto"
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  sendMessage()
                }
              }}
            />

            {ai.isStreaming ? (
              <ErpButton type="button" variant="danger" size="sm" onClick={stopStreaming}>
                <CircleStop className="h-4 w-4" />
              </ErpButton>
            ) : (
              <ErpButton type="submit" size="sm" disabled={!input.trim()}>
                <Send className="h-4 w-4" />
              </ErpButton>
            )}
          </ErpFormRaw>

          {ai.messages.length > 0 && !ai.isStreaming && (
            <ErpButtonRaw
              type="button"
              className="mt-2 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={runDeepAnalysis}
            >
              {t('AI_AnalyzeBackground')}
            </ErpButtonRaw>
          )}
        </ErpDiv>
      </ErpDiv>

      <AiActionApprovalModal
        approval={ai.approvalPrompt}
        onClose={() => dispatch(setApprovalPrompt(null))}
      />
    </ErpOffcanvas>
  )
}

export default AiCopilotDrawer
